import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import {
  Container, Typography, TextField, Button, Box, Paper, AppBar, Toolbar, CssBaseline,
  CircularProgress, Alert, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Tabs, Tab,
  IconButton, Chip, Card, CardContent, Grid, Avatar, Badge,
  Stack, Snackbar, Menu, MenuItem, ListItemIcon, ListItemText, Divider
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  AccountBalance as AccountBalanceIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Refresh as RefreshIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  RequestPage as RequestPageIcon,
  MonetizationOn as MonetizationOnIcon,
  AdminPanelSettings,
  Logout as LogoutIcon,
  Visibility as VisibilityIcon,
  ShoppingCart as ShoppingCartIcon,
  AccountBalanceWallet as WalletIcon,
  Menu as MenuIcon,
  VpnKey as PasswordIcon
} from '@mui/icons-material';
import io from 'socket.io-client';
import { authAPI, playersAPI, requestsAPI } from './api';
import createAppTheme from './theme';

// Tab Panel Component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// Dashboard Overview Component
const DashboardOverview = ({ socket, user }) => {
  const [stats, setStats] = useState({
    totalPlayers: 0,
    totalCoins: 0,
    pendingRequests: 0,
    activeGames: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const [playersRes, requestsRes] = await Promise.all([
        fetch(`/api/users/players`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }),
        fetch(`/api/requests/pending`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        })
      ]);
      
      const players = await playersRes.json();
      const requests = await requestsRes.json();
      
      setStats({
        totalPlayers: players.length,
        totalCoins: players.reduce((sum, player) => sum + (player.coins || 0), 0),
        pendingRequests: requests.length,
        activeGames: 0 // This would come from game service
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <CircularProgress />;

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ mb: 4 }}>
        📊 Dashboard Overview
      </Typography>
      
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Avatar sx={{ bgcolor: 'primary.main', mx: 'auto', mb: 2, width: 56, height: 56 }}>
                <PeopleIcon fontSize="large" />
              </Avatar>
              <Typography variant="h4" color="primary">
                {stats.totalPlayers}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Players
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Avatar sx={{ bgcolor: 'success.main', mx: 'auto', mb: 2, width: 56, height: 56 }}>
                <MonetizationOnIcon fontSize="large" />
              </Avatar>
              <Typography variant="h4" color="success.main">
                {stats.totalCoins.toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Coins in System
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Avatar sx={{ bgcolor: 'warning.main', mx: 'auto', mb: 2, width: 56, height: 56 }}>
                <RequestPageIcon fontSize="large" />
              </Avatar>
              <Typography variant="h4" color="warning.main">
                {stats.pendingRequests}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Pending Requests
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Avatar sx={{ bgcolor: 'info.main', mx: 'auto', mb: 2, width: 56, height: 56 }}>
                <WalletIcon fontSize="large" />
              </Avatar>
              <Typography variant="h4" color="info.main">
                {(user?.coins || 0).toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Admin Balance
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Quick Actions
          </Typography>
          <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
            <Button variant="contained" startIcon={<RefreshIcon />} onClick={fetchStats}>
              Refresh Stats
            </Button>
            <Button variant="outlined" startIcon={<VisibilityIcon />}>
              View Reports
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

// Players Management Tab
const PlayersTab = ({ socket }) => {
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [grantDialogOpen, setGrantDialogOpen] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [newPlayer, setNewPlayer] = useState({ username: '', password: '' });
  const [grantAmount, setGrantAmount] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchPlayers();
    if (socket) {
      socket.on('balanceUpdate', (data) => {
        setPlayers(prevPlayers =>
          prevPlayers.map(player =>
            player.id === data.userId ? { ...player, coins: data.newBalance } : player
          )
        );
      });
    }
  }, [socket]);

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const fetchPlayers = async () => {
    setLoading(true);
    try {
      const players = await playersAPI.getAllPlayers();
      setPlayers(players);
    } catch (err) {
      setError(err.message || "Failed to fetch players.");
    } finally {
      setLoading(false);
    }
  };

  const handleAddPlayer = async () => {
    if (!newPlayer.username || !newPlayer.password) return;
    try {
      await authAPI.register(newPlayer);
      showSnackbar('Player added successfully!', 'success');
      setAddDialogOpen(false);
      setNewPlayer({ username: '', password: '' });
      fetchPlayers();
    } catch (err) {
      showSnackbar(err.message || 'Failed to add player', 'error');
    }
  };

  const handleDeletePlayer = async () => {
    if (!selectedPlayer) return;
    try {
      // Note: There's no delete player API in the current backend, so this would need to be implemented
      showSnackbar('Delete player functionality not implemented yet', 'warning');
      setDeleteDialogOpen(false);
      setSelectedPlayer(null);
    } catch (err) {
      showSnackbar('Failed to delete player', 'error');
    }
  };

  const handleGrantCoins = async () => {
    if (!selectedPlayer || grantAmount <= 0) return;
    try {
      const response = await playersAPI.grantCoins(selectedPlayer.id, grantAmount);
      showSnackbar(response.message || 'Coins granted successfully!', 'success');
      setGrantDialogOpen(false);
      setSelectedPlayer(null);
      setGrantAmount(1);
      fetchPlayers();
    } catch (err) {
      showSnackbar(err.message || 'Failed to grant coins', 'error');
    }
  };

  const filteredPlayers = players.filter(player =>
    player.username.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PeopleIcon /> Players Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setAddDialogOpen(true)}
        >
          Add Player
        </Button>
      </Box>

      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          variant="outlined"
          label="Search Players"
          placeholder="Search by name..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Player</TableCell>
              <TableCell>ID</TableCell>
              <TableCell align="right">Balance</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredPlayers.map((player) => (
              <TableRow key={player.id}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar sx={{ bgcolor: 'primary.main', mr: 2, width: 32, height: 32 }}>
                      {player.username[0].toUpperCase()}
                    </Avatar>
                    {player.username}
                  </Box>
                </TableCell>
                <TableCell>{player.id}</TableCell>
                <TableCell align="right">
                  <Chip
                    icon={<MonetizationOnIcon />}
                    label={`${typeof player.coins === 'number' ? player.coins.toLocaleString() : parseFloat(player.coins || 0).toLocaleString()}`}
                    color="success"
                    size="small"
                  />
                </TableCell>
                <TableCell align="center">
                  <Stack direction="row" spacing={1} justifyContent="center">
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<TrendingUpIcon />}
                      onClick={() => {
                        setSelectedPlayer(player);
                        setGrantDialogOpen(true);
                      }}
                    >
                      Grant Coins
                    </Button>
                    <IconButton
                      color="error"
                      onClick={() => {
                        setSelectedPlayer(player);
                        setDeleteDialogOpen(true);
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialogs */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Player</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Username"
            fullWidth
            variant="outlined"
            value={newPlayer.username}
            onChange={(e) => setNewPlayer({ ...newPlayer, username: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Password"
            type="password"
            fullWidth
            variant="outlined"
            value={newPlayer.password}
            onChange={(e) => setNewPlayer({ ...newPlayer, password: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddPlayer} variant="contained">Add Player</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle color="error">Delete Player</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete player "{selectedPlayer?.username}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeletePlayer} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={grantDialogOpen} onClose={() => setGrantDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Grant Coins to {selectedPlayer?.username}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Enter the amount of coins to grant to {selectedPlayer?.username}.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Amount"
            type="number"
            fullWidth
            variant="outlined"
            value={grantAmount}
            onChange={(e) => setGrantAmount(parseFloat(e.target.value) || 1)}
            inputProps={{ min: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGrantDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleGrantCoins} variant="contained">Grant Coins</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// Admin Requests Tab - for admin to send requests to house
const AdminRequestsTab = ({ user, onUserUpdate, socket }) => {
  const [buyDialogOpen, setBuyDialogOpen] = useState(false);
  const [cashoutDialogOpen, setCashoutDialogOpen] = useState(false);
  const [buyAmount, setBuyAmount] = useState(100);
  const [cashoutAmount, setCashoutAmount] = useState(100);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [myRequests, setMyRequests] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchMyRequests();
    
    if (socket && user) {
      socket.on('balanceUpdate', (data) => {
        if (data.userId === user.id) {
          onUserUpdate({ ...user, coins: data.newBalance });
        }
      });
    }
  }, [socket, user, onUserUpdate]);

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const fetchMyRequests = async () => {
    setLoading(true);
    try {
      const requests = await requestsAPI.getMyRequests();
      setMyRequests(requests);
    } catch (err) {
      console.error('Error fetching requests:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleBuyCoins = async () => {
    if (buyAmount <= 0) return;
    try {
      await requestsAPI.createTopUpRequest(buyAmount, `Admin request for ${buyAmount} coins from House`);
      showSnackbar(`Request for ${buyAmount} coins submitted to House successfully!`, 'success');
      setBuyDialogOpen(false);
      setBuyAmount(100);
      fetchMyRequests();
    } catch (error) {
      showSnackbar('Failed to submit request: ' + (error.message || 'Unknown error'), 'error');
    }
  };

  const handleCashOut = async () => {
    if (cashoutAmount <= 0 || cashoutAmount > (user?.coins || 0)) return;
    try {
      await requestsAPI.createCashOutRequest(cashoutAmount, `Admin cash out request for ${cashoutAmount} coins`);
      showSnackbar(`Cash out request for ${cashoutAmount} coins submitted to House successfully!`, 'success');
      setCashoutDialogOpen(false);
      setCashoutAmount(100);
      fetchMyRequests();
    } catch (error) {
      showSnackbar('Failed to submit request: ' + (error.message || 'Unknown error'), 'error');
    }
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <AccountBalanceIcon /> Admin Requests
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ShoppingCartIcon /> Buy Coins from House
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Request coins from the House. The House will review and approve your request.
              </Typography>
              <Typography variant="h6" color="info.main" sx={{ mb: 2 }}>
                Current Balance: {(user?.coins || 0).toLocaleString()} coins
              </Typography>
              <Button
                variant="contained"
                startIcon={<ShoppingCartIcon />}
                onClick={() => setBuyDialogOpen(true)}
                fullWidth
              >
                Request Coins from House
              </Button>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TrendingDownIcon /> Cash Out to House
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Convert your coins back to cash through the House.
              </Typography>
              <Typography variant="h6" color="success.main" sx={{ mb: 2 }}>
                Available: {(user?.coins || 0).toLocaleString()} coins
              </Typography>
              <Button
                variant="contained"
                color="warning"
                startIcon={<TrendingDownIcon />}
                onClick={() => setCashoutDialogOpen(true)}
                fullWidth
                disabled={(user?.coins || 0) === 0}
              >
                Cash Out Coins
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* My Requests */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            My Requests
          </Typography>
          {loading ? (
            <CircularProgress />
          ) : myRequests.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No requests found.
            </Typography>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Request ID</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {myRequests.map((request) => (
                    <TableRow key={request.id}>
                      <TableCell>#{request.id}</TableCell>
                      <TableCell>
                        <Chip
                          label={
                            request.type === 'buy_coins' ? 'Buy Coins' : 
                            request.type === 'cash_out' ? 'Cash Out' : 
                            request.type === 'admin_buy_coins' ? 'Admin Buy Coins' : 
                            request.type
                          }
                          color={
                            request.type === 'buy_coins' ? 'primary' : 
                            request.type === 'cash_out' ? 'warning' : 
                            request.type === 'admin_buy_coins' ? 'info' : 
                            'default'
                          }
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{request.amount} coins</TableCell>
                      <TableCell>
                        <Chip
                          label={request.status}
                          color={
                            request.status === 'approved' ? 'success' :
                            request.status === 'rejected' ? 'error' : 'warning'
                          }
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{new Date(request.createdAt).toLocaleDateString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Buy Coins Dialog */}
      <Dialog open={buyDialogOpen} onClose={() => setBuyDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Request Coins from House</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Enter the amount of coins you want to request from the House.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Amount"
            type="number"
            fullWidth
            variant="outlined"
            value={buyAmount}
            onChange={(e) => setBuyAmount(parseFloat(e.target.value) || 100)}
            inputProps={{ min: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBuyDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleBuyCoins} variant="contained">Submit Request</Button>
        </DialogActions>
      </Dialog>

      {/* Cash Out Dialog */}
      <Dialog open={cashoutDialogOpen} onClose={() => setCashoutDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Cash Out Coins</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Enter the amount of coins you want to cash out. Available: {(user?.coins || 0).toLocaleString()} coins
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Amount"
            type="number"
            fullWidth
            variant="outlined"
            value={cashoutAmount}
            onChange={(e) => setCashoutAmount(parseFloat(e.target.value) || 100)}
            inputProps={{ min: 1, max: user?.coins || 0 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCashoutDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCashOut} variant="contained" color="warning">Submit Cash Out</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// Player Requests Tab
const PlayerRequestsTab = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchPlayerRequests();
  }, []);

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const fetchPlayerRequests = async () => {
    setLoading(true);
    try {
      const requests = await requestsAPI.getPendingRequests();
      setRequests(requests);
    } catch (err) {
      setError(err.message || "Failed to fetch player requests.");
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (requestId, requestType) => {
    try {
      await requestsAPI.approveRequest(requestId, requestType);
      showSnackbar('Request approved successfully!', 'success');
      fetchPlayerRequests();
    } catch (err) {
      showSnackbar(err.message || 'Failed to approve request', 'error');
    }
  };

  const handleReject = async (requestId) => {
    try {
      await requestsAPI.rejectRequest(requestId, 'Rejected by admin');
      showSnackbar('Request rejected successfully!', 'success');
      fetchPlayerRequests();
    } catch (err) {
      showSnackbar(err.message || 'Failed to reject request', 'error');
    }
  };

  const filteredRequests = requests.filter(request => {
    const username = request.user?.username?.toLowerCase() || '';
    const type = request.type?.toLowerCase() || '';
    const query = searchQuery.toLowerCase();
    return username.includes(query) || type.includes(query);
  });

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      
      <Typography variant="h4" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <RequestPageIcon /> Player Requests
        <Badge badgeContent={requests.length} color="warning" sx={{ ml: 2 }} />
      </Typography>

      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          variant="outlined"
          label="Search Requests"
          placeholder="Search by player name or request type..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </Box>

      {filteredRequests.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <RequestPageIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No pending player requests
            </Typography>
            <Typography variant="body2" color="text.secondary">
              All requests have been processed or your search returned no results.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Request ID</TableCell>
                <TableCell>Player</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Date</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRequests.map((request) => (
                <TableRow key={request.id}>
                  <TableCell>#{request.id}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar sx={{ bgcolor: 'primary.main', mr: 2, width: 32, height: 32 }}>
                        {request.user?.username?.[0]?.toUpperCase() || 'U'}
                      </Avatar>
                      {request.user?.username || 'Unknown'}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={request.type === 'buy_coins' ? 'Buy Coins' : 'Cash Out'}
                      color={request.type === 'buy_coins' ? 'success' : 'error'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{request.amount} coins</TableCell>
                  <TableCell>{new Date(request.createdAt).toLocaleDateString()}</TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={1} justifyContent="center">
                      <Button
                        variant="contained"
                        color="success"
                        size="small"
                        onClick={() => handleApprove(request.id, request.type)}
                      >
                        Approve
                      </Button>
                      <Button
                        variant="contained"
                        color="error"
                        size="small"
                        onClick={() => handleReject(request.id)}
                      >
                        Reject
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// Main Admin Dashboard Component
const AdminDashboard = ({ user, socket, onLogout, onUserUpdate }) => {
  const [tabValue, setTabValue] = useState(0);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [changePasswordDialogOpen, setChangePasswordDialogOpen] = useState(false);
  const [passwordData, setPasswordData] = useState({ currentPassword: '', newPassword: '' });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  
  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };
  
  const handleMenuOpen = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };
  
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };
  
  const handleChangePasswordClick = () => {
    handleMenuClose();
    setChangePasswordDialogOpen(true);
  };
  
  const handleChangePassword = async () => {
    if (!passwordData.currentPassword || !passwordData.newPassword) {
      showSnackbar('Please fill in all fields', 'error');
      return;
    }
    
    try {
      await authAPI.changePassword(passwordData.currentPassword, passwordData.newPassword);
      showSnackbar('Password changed successfully!', 'success');
      setChangePasswordDialogOpen(false);
      setPasswordData({ currentPassword: '', newPassword: '' });
    } catch (error) {
      showSnackbar(error.message || 'Failed to change password', 'error');
    }
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="static">
        <Toolbar>
          <AdminPanelSettings sx={{ mr: 2 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Admin Dashboard
          </Typography>
          <Chip
            icon={<WalletIcon />}
            label={`${(user?.coins || 0).toLocaleString()} coins`}
            color="primary"
            variant="outlined"
            sx={{ mr: 2, color: 'white', borderColor: 'white' }}
          />
          <Typography variant="body1" sx={{ mr: 2 }}>
            Welcome, {user?.username}!
          </Typography>
          
          <IconButton 
            color="inherit" 
            onClick={handleMenuOpen}
            aria-controls="user-menu"
            aria-haspopup="true"
          >
            <MenuIcon />
          </IconButton>
          
          <Menu
            id="user-menu"
            anchorEl={menuAnchorEl}
            keepMounted
            open={Boolean(menuAnchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem onClick={handleChangePasswordClick}>
              <ListItemIcon>
                <PasswordIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Change Password" />
            </MenuItem>
            <Divider />
            <MenuItem onClick={onLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Logout" />
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="admin tabs">
          <Tab label="Dashboard" icon={<DashboardIcon />} />
          <Tab label="Players" icon={<PeopleIcon />} />
          <Tab label="Admin Requests" icon={<AccountBalanceIcon />} />
          <Tab label="Player Requests" icon={<RequestPageIcon />} />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <DashboardOverview socket={socket} user={user} />
      </TabPanel>
      <TabPanel value={tabValue} index={1}>
        <PlayersTab socket={socket} />
      </TabPanel>
      <TabPanel value={tabValue} index={2}>
        <AdminRequestsTab user={user} onUserUpdate={onUserUpdate} socket={socket} />
      </TabPanel>
      <TabPanel value={tabValue} index={3}>
        <PlayerRequestsTab />
      </TabPanel>
      
      <Dialog open={changePasswordDialogOpen} onClose={() => setChangePasswordDialogOpen(false)}>
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Current Password"
            type="password"
            fullWidth
            variant="outlined"
            value={passwordData.currentPassword}
            onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="New Password"
            type="password"
            fullWidth
            variant="outlined"
            value={passwordData.newPassword}
            onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChangePasswordDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleChangePassword} variant="contained">Change Password</Button>
        </DialogActions>
      </Dialog>
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// Login Component
const Login = ({ onLogin }) => {
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!credentials.username || !credentials.password) return;

    setLoading(true);
    setError('');

    try {
      const response = await authAPI.login(credentials.username, credentials.password);
      if (response.user && response.user.role === 'admin') {
        onLogin({ ...response.user, token: response.access_token });
      } else {
        setError('Access denied. Admin privileges required.');
      }
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
            <AdminPanelSettings sx={{ fontSize: 40, mr: 2, color: 'primary.main' }} />
            <Typography variant="h4" component="h1">
              Admin Login
            </Typography>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <form onSubmit={handleSubmit}>
            <TextField
              margin="normal"
              required
              fullWidth
              label="Username"
              autoComplete="username"
              autoFocus
              value={credentials.username}
              onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              label="Password"
              type="password"
              autoComplete="current-password"
              value={credentials.password}
              onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Sign In'}
            </Button>
          </form>
        </Paper>
      </Box>
    </Container>
  );
};

// Main App Component
const App = () => {
  const [user, setUser] = useState(null);
  const [socket, setSocket] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const theme = createAppTheme('dark');

  useEffect(() => {
    checkAuthStatus();
  }, []);

  useEffect(() => {
    if (user) {
      const newSocket = io('/');
      setSocket(newSocket);

      newSocket.on('connect', () => {
        console.log('Connected to server');
        newSocket.emit('join', { userId: user.id, role: user.role });
      });

      return () => {
        newSocket.disconnect();
      };
    }
  }, [user]);

  const checkAuthStatus = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const response = await authAPI.getProfile();
        if (response.role === 'admin') {
          setUser(response);
        } else {
          localStorage.removeItem('token');
        }
      } catch (error) {
        localStorage.removeItem('token');
      }
    }
    setLoading(false);
  };

  const handleLogin = (userData) => {
    setUser(userData);
    localStorage.setItem('token', userData.token);
  };

  const handleLogout = () => {
    setUser(null);
    setSocket(null);
    authAPI.logout();
  };

  const handleUserUpdate = (updatedUser) => {
    setUser(updatedUser);
  };

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <CircularProgress />
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        {user ? (
          <AdminDashboard 
            user={user} 
            socket={socket} 
            onLogout={handleLogout}
            onUserUpdate={handleUserUpdate}
          />
        ) : (
          <Login onLogin={handleLogin} />
        )}
      </Router>
    </ThemeProvider>
  );
};

export default App;
